/**
 * Organization Store - Pinia store for managing multi-tenant organization data
 * Handles organization settings, billing, and tenant management
 */

import { defineStore } from "pinia";
import { ref, computed } from "vue";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  addDoc,
  updateDoc,
  onSnapshot,
  query,
  where,
  serverTimestamp,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions, COLLECTIONS } from "../services/firebase";
import { useUserStore } from "./user";

export const useOrganizationStore = defineStore("organization", () => {
  // State
  const currentOrganization = ref(null);
  const userOrganizations = ref([]);
  const loading = ref(false);
  const error = ref(null);

  // Getters
  const isOwner = computed(() => {
    const userStore = useUserStore();
    return userStore.role === "owner";
  });

  const isAdmin = computed(() => {
    const userStore = useUserStore();
    return ["owner", "admin"].includes(userStore.role);
  });

  const canManageUsers = computed(() => {
    return isAdmin.value;
  });

  const canManageBilling = computed(() => {
    return isOwner.value;
  });

  const subscriptionTier = computed(() => {
    return currentOrganization.value?.plan?.tier || "free";
  });

  const subscriptionStatus = computed(() => {
    return currentOrganization.value?.plan?.status || "trial";
  });

  const isSubscriptionActive = computed(() => {
    return ["active", "trial"].includes(subscriptionStatus.value);
  });

  // Actions
  const loadCurrentOrganization = async (orgId) => {
    try {
      loading.value = true;
      error.value = null;

      const orgDoc = await getDoc(doc(db, COLLECTIONS.ORGS, orgId));
      if (orgDoc.exists()) {
        currentOrganization.value = {
          id: orgDoc.id,
          ...orgDoc.data(),
        };
      }
    } catch (err) {
      error.value = err.message;
      console.error("Error loading organization:", err);
    } finally {
      loading.value = false;
    }
  };

  const loadUserOrganizations = async (userId) => {
    try {
      loading.value = true;
      error.value = null;

      // Get user's organizations from user profile
      const userDoc = await getDoc(doc(db, COLLECTIONS.USERS, userId));
      if (!userDoc.exists()) {
        userOrganizations.value = [];
        return;
      }

      const orgIds = userDoc.data().organizations || [];
      
      // Load each organization
      const orgs = [];
      for (const orgId of orgIds) {
        const orgDoc = await getDoc(doc(db, COLLECTIONS.ORGS, orgId));
        if (orgDoc.exists()) {
          orgs.push({
            id: orgDoc.id,
            ...orgDoc.data(),
          });
        }
      }

      userOrganizations.value = orgs;
    } catch (err) {
      error.value = err.message;
      console.error("Error loading user organizations:", err);
    } finally {
      loading.value = false;
    }
  };

  const createOrganization = async (name, planTier = 'free') => {
    try {
      loading.value = true;
      error.value = null;

      // Use Cloud Function to create organization
      const createOrgFn = httpsCallable(functions, 'createOrganizationV2');
      const result = await createOrgFn({
        name,
        planTier
      });

      const orgId = result.data.orgId;
      
      // Switch to the new organization
      const userStore = useUserStore();
      await userStore.switchOrganization(orgId);
      
      return orgId;
    } catch (err) {
      error.value = err.message;
      console.error("Error creating organization:", err);
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const switchOrganization = async (orgId) => {
    try {
      loading.value = true;
      error.value = null;

      const userStore = useUserStore();
      await userStore.switchOrganization(orgId);
      await loadCurrentOrganization(orgId);

      return true;
    } catch (err) {
      error.value = err.message;
      console.error("Error switching organization:", err);
      throw err;
    } finally {
      loading.value = false;
    }
  };



  const updateOrganizationSettings = async (settings) => {
    if (!currentOrganization.value?.id) return;

    try {
      loading.value = true;
      error.value = null;

      await updateDoc(doc(db, COLLECTIONS.ORGS, currentOrganization.value.id), {
        ...settings,
        updatedAt: serverTimestamp(),
      });

      currentOrganization.value = {
        ...currentOrganization.value,
        ...settings,
      };
    } catch (err) {
      error.value = err.message;
      console.error("Error updating organization:", err);
    } finally {
      loading.value = false;
    }
  };

  const updateAutoResponseRules = async (rules) => {
    if (!currentOrganization.value?.id) return;

    try {
      loading.value = true;
      error.value = null;

      await updateOrganizationSettings({
        autoResponses: rules,
      });

      return true;
    } catch (err) {
      error.value = err.message;
      console.error("Error updating auto-response rules:", err);
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const initializeAISettings = async () => {
    if (!currentOrganization.value?.id) return;

    try {
      loading.value = true;
      error.value = null;

      const initFn = httpsCallable(functions, "initializeAISettingsV2");
      const result = await initFn();

      // Reload organization to get updated settings
      await loadCurrentOrganization(currentOrganization.value.id);

      return result.data;
    } catch (err) {
      error.value = err.message;
      console.error("Error initializing AI settings:", err);
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const clearError = () => {
    error.value = null;
  };

  return {
    // State
    currentOrganization,
    userOrganizations,
    loading,
    error,

    // Getters
    isOwner,
    isAdmin,
    canManageUsers,
    canManageBilling,
    subscriptionTier,
    subscriptionStatus,
    isSubscriptionActive,

    // Actions
    loadCurrentOrganization,
    loadUserOrganizations,
    createOrganization,
    switchOrganization,
    updateOrganizationSettings,
    updateAutoResponseRules,
    initializeAISettings,
    clearError,
  };
});
